/**
 * Edition navigation UI — calendar window uses start_date/end_date only (same as Edition.getStatus).
 */
import moment from 'moment';

/** @typedef {'upcoming' | 'active' | 'past'} EditionCalendarStatus */

/**
 * Where “now” sits relative to the edition window [start_date, end_date] (day granularity).
 * Does not use event_* dates.
 * @returns {EditionCalendarStatus|null}
 */
export function getEditionCalendarStatus(edition) {
    if (!edition) {
        return null;
    }
    const now = moment();
    const start = moment(edition.start_date);
    const end = moment(edition.end_date);

    if (!start.isValid() || !end.isValid()) {
        return 'upcoming';
    }
    if (now.isBefore(start, 'day')) {
        return 'upcoming';
    }
    if (now.isAfter(end, 'day')) {
        return 'past';
    }
    return 'active';
}

export function findEditionById(state, editionId) {
    if (!editionId) {
        return null;
    }
    const want = String(editionId);
    const editions = state.editionNavigation?.editions;
    const list = Array.isArray(editions) ? editions : [];
    return list.find((e) => String(e._id || e.id) === want) || null;
}

function hasEditionId(state, editionId) {
    return Boolean(findEditionById(state, editionId));
}

/** Mutations blocked only when viewing a past edition that is not the configured current one. */
export function isEditionNavigationReadonlyPast(state) {
    const selected = state.editionNavigation?.selectedEditionId;
    const configured = state.settings?.setting?.edition;
    if (!selected || !configured || !hasEditionId(state, configured) || String(selected) === String(configured)) {
        return false;
    }
    const edition = findEditionById(state, selected);
    if (!edition) {
        return false;
    }
    return getEditionCalendarStatus(edition) === 'past';
}

/**
 * @returns {'past' | 'future' | null} Banner to show (same placement as health resume), or null.
 */
export function getEditionNavigationBannerKind(state) {
    const selected = state.editionNavigation?.selectedEditionId;
    const configured = state.settings?.setting?.edition;
    if (!selected || !configured || !hasEditionId(state, configured) || String(selected) === String(configured)) {
        return null;
    }
    const edition = findEditionById(state, selected);
    if (!edition) {
        return null;
    }
    const cal = getEditionCalendarStatus(edition);
    if (cal === 'past') {
        return 'past';
    }
    if (cal === 'upcoming') {
        return 'future';
    }
    return null;
}
